import { z } from 'zod';
import { RENTAL_BILLING_INTERVALS } from '../constants/rental-states.js';
import { moneyCentsSchema, uuidSchema } from './common.js';
import { creditTenderInputSchema } from './gift-card.js';
import { orderItemInputSchema } from './order.js';
import { rentalDateSchema } from './rental.js';
/**
 * Combined purchase + rental checkout for the counter till (t0943) — staff
 * can put purchase lines and one or more rentals on the same ticket and take
 * a single tender for the lot. Terms still live on the rental itself, the
 * same as `createRentalSchema`; this just lets several open at once, each
 * carrying its own units and terms, alongside ordinary purchase lines.
 */
export const checkoutRentalGroupSchema = z.object({
    unitIds: z.array(uuidSchema).min(1).max(50),
    startDate: rentalDateSchema,
    dueDate: rentalDateSchema.nullable().default(null),
    feeCents: moneyCentsSchema,
    billingInterval: z.enum(RENTAL_BILLING_INTERVALS).default('one_time'),
    depositCents: moneyCentsSchema.default(0),
    notes: z.string().trim().max(2000).nullable().default(null),
});
/**
 * A tender staff already collected outside the portal — cash, a card machine
 * swipe, a check, or Zelle. Settles the order and stamps every rental's fee
 * and deposit in the same step; nothing is left pending.
 */
const checkoutRecordPaymentSchema = z.object({
    mode: z.literal('record'),
    provider: z.enum(['cash', 'external_card', 'check', 'zelle']),
    reference: z.string().trim().max(120).nullable().default(null),
});
/**
 * An integrated card charge for the purchases-and-fees portion of the ticket
 * (t0943) — a newly entered card, a connected reader, or a card already on
 * the customer's file. A rental deposit is never part of this charge: Stripe
 * cannot mix an immediate charge with an authorization hold in one
 * PaymentIntent, so a deposit always rides its own hold, placed right after
 * (automatically for a saved card, or as a guided next step on the rental
 * itself for a newly entered card or a reader).
 */
const checkoutCardPaymentSchema = z.object({
    mode: z.literal('card'),
    method: z.enum(['new_card', 'reader', 'saved_card']),
    /** Required for `saved_card` — the local `user_payment_methods.id`, never
     *  a raw Stripe `pm_…` id. */
    paymentMethodId: uuidSchema.nullable().default(null),
});
const checkoutPaymentSchema = z
    .discriminatedUnion('mode', [checkoutRecordPaymentSchema, checkoutCardPaymentSchema])
    .nullable()
    .default(null);
export const posCheckoutSchema = z
    .object({
    customerId: uuidSchema.nullable().default(null), // null = walk-in; rentals require one
    locationId: uuidSchema,
    items: z.array(orderItemInputSchema).max(100).default([]),
    rentals: z.array(checkoutRentalGroupSchema).max(10).default([]),
    note: z.string().trim().max(1000).nullable().default(null),
    /** A staleness guard, not the figure that gets charged — the server
     *  recomputes the real total (tax included) from scratch and 409s if it
     *  disagrees with what the till was showing when "Take payment" was
     *  pressed. */
    expectedTotalCents: moneyCentsSchema,
    /** Gift cards and store credit spent on the purchase lines (never on rental
     *  fees or deposits). What is left is `payment`; when credit covers the
     *  whole purchase and there are no rental fees, `payment` may be null. */
    credits: z.array(creditTenderInputSchema).max(4).default([]),
    payment: checkoutPaymentSchema,
})
    .refine((v) => v.items.length > 0 || v.rentals.length > 0, {
    message: 'The cart is empty',
    path: ['items'],
})
    .refine((v) => v.rentals.length === 0 || v.customerId !== null, {
    message: 'A rental needs a customer on file — walk-ins are purchases only',
    path: ['customerId'],
})
    .refine((v) => v.payment?.mode !== 'card' ||
    v.payment.method !== 'saved_card' ||
    v.payment.paymentMethodId !== null, { message: 'Choose a saved card to charge', path: ['payment', 'paymentMethodId'] });
export const checkoutRentalResultSchema = z.object({
    id: uuidSchema,
    rentalNumber: z.string(),
    /** So the till (and the receipt) can tell staff which of the rentals it
     *  just opened still need a deposit hold placed. */
    depositCents: moneyCentsSchema,
});
export const posCheckoutResultSchema = z.object({
    /** Null unless a card charge was opened for this ticket — a recorded
     *  tender or an unpaid sale settles everything inline and needs no
     *  standing record of its own beyond the order/rentals it created. */
    checkoutId: uuidSchema.nullable(),
    /** `card_pending` only while a newly entered card or a reader is still
     *  waiting to be confirmed — `POST /checkout/:id/settle` (or the webhook)
     *  moves it to `paid`. */
    status: z.enum(['paid', 'unpaid', 'card_pending']),
    orderId: uuidSchema.nullable(),
    orderNumber: z.string().nullable(),
    rentals: z.array(checkoutRentalResultSchema),
    orderCents: moneyCentsSchema,
    feeCents: moneyCentsSchema,
    depositCents: moneyCentsSchema,
    totalCents: moneyCentsSchema,
    /** Gift card and store credit taken off the purchase lines. */
    creditCents: moneyCentsSchema.default(0),
    /** Set only for a newly entered card or a reader, while `status` is still
     *  `card_pending` — what the browser mounts Stripe.js against. */
    clientSecret: z.string().nullable(),
    publishableKey: z.string().nullable(),
});
/** Switches a still-pending card checkout to a tender staff collected some
 *  other way — a decline, or a customer who changed their mind about paying
 *  by card. Cancels whatever PaymentIntent was open and records the tender
 *  instead, the same as if "record" had been chosen up front. */
export const checkoutTenderSchema = z.object({
    provider: z.enum(['cash', 'external_card', 'check', 'zelle']),
    reference: z.string().trim().max(120).nullable().default(null),
});
//# sourceMappingURL=checkout.js.map